import { abortError } from './navAbort.mjs';
// perf-reads: one in-flight GET per (url, Authorization). A duplicate GET fired while the first is pending (opening an order mounts
// several panels that read the same URL) shares that one fetch; every caller gets its OWN Response clone, so each parses its own
// body and none can see another's mutations. A non-GET passing through clears the map before AND after it runs, so a read issued
// during or after a write never joins a pre-write read.
// abort-reads: a caller's AbortSignal cancels only THAT caller (its promise rejects with an AbortError). The shared fetch has its own
// controller and is aborted only when every subscriber has left; a subscriber with no signal keeps it alive until it settles.
// Not coalesced: non-GET, or a request with a body.
// Round 1 (C3): `keep(url)` (opt-in, master-data lists) also keeps a SETTLED ok GET for keepMs, so Settings tabs, pickers and drawers
// opened one after another reuse one read. Same per-(url, Authorization) key, each caller its own clone; every non-GET clears it with the
// in-flight map (write invalidation), and keepMs bounds what another user / tab / process changed meanwhile (display staleness <= keepMs).

export function makeCoalescingFetch(doFetch, { keep = () => false, keepMs = 30000, now = () => Date.now() } = {}) {
  const inflight = new Map();
  const recent = new Map();   // key -> { r: Response, at }
  let epoch = 0;   // bumped by every write (start and end): a read that overlapped a write is never kept
  const wipe = () => { epoch++; inflight.clear(); recent.clear(); };
  const subscribe = (entry, signal) => new Promise((resolve, reject) => {
    entry.refs++;
    let settled = false;
    const leave = () => { if (settled) return; settled = true; if (signal) signal.removeEventListener('abort', onAbort); if (--entry.refs === 0 && !entry.done) { entry.ctrl.abort(); entry.drop(); } };
    const onAbort = () => { leave(); reject(abortError()); };
    if (signal) { if (signal.aborted) return onAbort(); signal.addEventListener('abort', onAbort, { once: true }); }
    entry.p.then((r) => { if (settled) return; settled = true; if (signal) signal.removeEventListener('abort', onAbort); resolve(r && typeof r.clone === 'function' ? r.clone() : r); },
      (e) => { if (settled) return; settled = true; if (signal) signal.removeEventListener('abort', onAbort); reject(e); });
  });
  const fetchShared = (url, init = {}) => {
    const method = String(init.method || 'GET').toUpperCase();
    if (method !== 'GET') { wipe(); return Promise.resolve().then(() => doFetch(url, init)).finally(wipe); }
    if (init.body != null) return doFetch(url, init);
    if (init.signal && init.signal.aborted) return Promise.reject(abortError());   // a left page never starts (or joins) a fetch
    const { signal, ...rest } = init;
    const h = init.headers || {};
    const key = `${url}|${h.Authorization || h.authorization || ''}`;
    const kept = recent.get(key);
    if (kept && now() - kept.at < keepMs) return Promise.resolve(kept.r.clone());
    let entry = inflight.get(key);
    if (!entry) {
      const ctrl = new AbortController();
      entry = { ctrl, refs: 0, done: false, drop: () => { if (inflight.get(key) === entry) inflight.delete(key); } };
      entry.p = Promise.resolve().then(() => doFetch(url, { ...rest, signal: ctrl.signal }));
      const e = entry; const done = () => { e.done = true; e.drop(); };
      const at0 = epoch;
      e.p.then((r) => { done(); if (at0 === epoch && r && r.ok && typeof r.clone === 'function' && keep(url)) recent.set(key, { r: r.clone(), at: now() }); }, done);
      inflight.set(key, entry);
    }
    return subscribe(entry, signal);
  };
  fetchShared.clear = wipe;
  fetchShared.size = () => inflight.size;
  return fetchShared;
}
